import type React from "react";
import { PERSON_DETAIL_TAB_KEY, PERSON_DETAIL_TAB_UNKNOWN_ORDER } from "./personDetailValues";

const HIDDEN_FOR_PROSPECT_TAB_KEYS = new Set(["statement", "contributions", "openitems", "restPeriods", "priceAdjustments"]);

const PERSON_DETAIL_TAB_ORDER: Record<string, number> = {
  overview: 0,
  profile: 10,
  contracts: 20,
  restPeriods: 22,
  priceAdjustments: 24,
  wallets: 30,
  articleSales: 40,
  promotions: 50,
  statement: 55,
  contributions: 60,
  openitems: 70,
  payments: 80,
  files: 90,
  changes: 100,
};

function normalizePersonDetailTabKey(key: string): string {
  if (key === PERSON_DETAIL_TAB_KEY.legacyDocuments || key === PERSON_DETAIL_TAB_KEY.legacyDocument) return PERSON_DETAIL_TAB_KEY.files;
  return key;
}

type PersonDetailTabItem = {
  key?: React.Key;
};

export type PersonDetailTabVisibility = {
  hideProspectDetailSections: boolean;
  canReadContracts: boolean;
  canReadRestPeriods: boolean;
  canReadPriceAdjustments: boolean;
  canReadFinance: boolean;
  canReadOpenItems: boolean;
  canReadPayments: boolean;
  canReadArticleSales: boolean;
  canReadMemberPromotions: boolean;
  canReadDocuments: boolean;
  canReadChangeHistory: boolean;
};

export function isPersonDetailTabVisible(key: string, visibility: PersonDetailTabVisibility): boolean {
  if (visibility.hideProspectDetailSections && HIDDEN_FOR_PROSPECT_TAB_KEYS.has(key)) return false;
  if (key === PERSON_DETAIL_TAB_KEY.contracts) return visibility.canReadContracts;
  if (key === PERSON_DETAIL_TAB_KEY.restPeriods) return visibility.canReadRestPeriods;
  if (key === PERSON_DETAIL_TAB_KEY.priceAdjustments) return visibility.canReadPriceAdjustments;
  if (key === PERSON_DETAIL_TAB_KEY.contributions || key === PERSON_DETAIL_TAB_KEY.wallets) return visibility.canReadFinance;
  if (key === PERSON_DETAIL_TAB_KEY.openItems || key === PERSON_DETAIL_TAB_KEY.statement) return visibility.canReadOpenItems;
  if (key === PERSON_DETAIL_TAB_KEY.payments) return visibility.canReadPayments;
  if (key === PERSON_DETAIL_TAB_KEY.articleSales) return visibility.canReadArticleSales;
  if (key === PERSON_DETAIL_TAB_KEY.promotions) return visibility.canReadMemberPromotions;
  if (key === PERSON_DETAIL_TAB_KEY.files) return visibility.canReadDocuments;
  if (key === PERSON_DETAIL_TAB_KEY.changes) return visibility.canReadChangeHistory;
  return true;
}

export function orderPersonDetailTabs<T extends PersonDetailTabItem>(items: T[]): T[] {
  return [...items].sort(
    (a, b) =>
      (PERSON_DETAIL_TAB_ORDER[String(a.key)] ?? PERSON_DETAIL_TAB_UNKNOWN_ORDER) -
      (PERSON_DETAIL_TAB_ORDER[String(b.key)] ?? PERSON_DETAIL_TAB_UNKNOWN_ORDER)
  );
}

export function getVisiblePersonDetailTabs<T extends PersonDetailTabItem>(
  items: T[],
  visibility: PersonDetailTabVisibility
): T[] {
  return orderPersonDetailTabs(
    items.filter((item) => isPersonDetailTabVisible(String(item.key), visibility))
  );
}

export function getActivePersonDetailTab<T extends PersonDetailTabItem>(
  items: T[],
  visibility: PersonDetailTabVisibility,
  requestedTab: string,
  fallbackTab = "overview"
): string {
  const visibleItems = getVisiblePersonDetailTabs(items, visibility);
  const normalizedRequestedTab = normalizePersonDetailTabKey(requestedTab);
  return visibleItems.some((item) => item.key === normalizedRequestedTab)
    ? normalizedRequestedTab
    : String(visibleItems[0]?.key ?? fallbackTab);
}

export function getPersonDetailTabState<T extends PersonDetailTabItem>(
  items: T[],
  visibility: PersonDetailTabVisibility,
  requestedTab: string,
  fallbackTab = "overview"
): { visibleTabItems: T[]; activeTabKey: string } {
  const visibleTabItems = getVisiblePersonDetailTabs(items, visibility);
  const normalizedRequestedTab = normalizePersonDetailTabKey(requestedTab);
  const activeTabKey = visibleTabItems.some((item) => item.key === normalizedRequestedTab)
    ? normalizedRequestedTab
    : String(visibleTabItems[0]?.key ?? fallbackTab);
  return { visibleTabItems, activeTabKey };
}

/**
 * Sechs Reiter der Akte, die heutigen Reiter darin als Segmente. Die Adressen bleiben die der
 * Segmente (`?tab=openitems`), damit Lesezeichen und Sprünge aus anderen Seiten weiter treffen.
 */
export const PERSON_DETAIL_TAB_GROUPS: ReadonlyArray<{ key: string; labelKey: string; sections: readonly string[] }> = [
  { key: "overview", labelKey: "Overview", sections: ["overview"] },
  { key: "profile", labelKey: "Person", sections: ["profile"] },
  { key: "contracts", labelKey: "Contracts", sections: ["contracts", "restPeriods", "priceAdjustments", "wallets", "promotions"] },
  { key: "account", labelKey: "Person account tab", sections: ["statement", "contributions", "openitems", "payments", "articleSales"] },
  { key: "files", labelKey: "Digital file", sections: ["files"] },
  { key: "changes", labelKey: "Person history tab", sections: ["changes"] },
];

export type PersonDetailTabGroup<T> = { key: string; labelKey: string; sections: T[] };

/** Sichtbare Segmente je Gruppe; Gruppen ohne sichtbares Segment entfallen. */
export function groupPersonDetailTabs<T extends PersonDetailTabItem>(visibleItems: T[], activeSectionKey: string): {
  groups: PersonDetailTabGroup<T>[];
  activeGroupKey: string;
} {
  const groups = PERSON_DETAIL_TAB_GROUPS
    .map((group) => ({
      key: group.key,
      labelKey: group.labelKey,
      sections: group.sections
        .map((section) => visibleItems.find((item) => String(item.key) === section))
        .filter((item): item is T => item !== undefined),
    }))
    .filter((group) => group.sections.length > 0);
  const activeGroup = groups.find((group) => group.sections.some((section) => String(section.key) === activeSectionKey)) ?? groups[0];
  return { groups, activeGroupKey: activeGroup?.key ?? "overview" };
}
